// Config for the "Start New Game" wizard: competition categories, team-size bands,
// base prices and the duration adjustment used to build the indicative price shown
// on the Package & Pricing / Review steps.
//
// Base prices below are NOT invented for this wizard - they're pulled straight from
// the "Done-for-You" (managed) and "Do-It-Yourself" (self-scoring) tables already
// live on /pricing-plan (see PricingPlanForClub.js and PricingPlanForLeagues.js).
// The 6-Month / "1-3 Months" tier from those tables is treated as the Season
// (no-adjustment) baseline; Short/Extended apply the -10%/+10% duration adjustment.
// Partnership Services, Match Contest, National Tournament and Global Tournament all
// have real base prices too - they're still routed through isReviewRequired() below
// because those categories/packages need a manual admin review after signup, not
// because the price is unknown.

export const COMPETITION_CATEGORIES = [
  {
    value: 'club_competition',
    label: 'Club Competition',
    description: 'For competitions run by a single club across teams and divisions.',
  },
  {
    value: 'community_league',
    label: 'Community League',
    description: 'For local leagues involving multiple clubs in your area.',
  },
  {
    value: 'association_league',
    label: 'Association League',
    description: 'For leagues governed by a regional or state association.',
  },
  {
    value: 'premier_state_competition',
    label: 'Premier or State Competition',
    description: 'For top-tier or state-wide competitions.',
  },
  {
    value: 'national_tournament',
    label: 'National Tournament',
    description: 'For national-level tournaments with teams from across the country.',
  },
  {
    value: 'global_tournament',
    label: 'Global Tournament',
    description: 'For international tournaments with teams from around the world.',
  },
  // {
  //   value: 'match_contest',
  //   label: 'Match Contest',
  //   description: 'For single matches or tipping style competitions.',
  // },
]

// Categories that use the shared "League and major competition" flow (Format step,
// single Season Package, 1-8/9-16/17+ bands).
export const LEAGUE_CATEGORIES = [
  'community_league',
  'association_league',
  'premier_state_competition',
  'national_tournament',
  'global_tournament',
]

// Categories the doc calls out for "Create Account & Request Review" instead of an
// instant, payable price. Match Contest is a fixed, instantly-priced package (see
// MATCH_CONTEST_PACKAGE below), so it's excluded from this list.
export const REVIEW_REQUIRED_CATEGORIES = ['national_tournament', 'global_tournament']

// Existing league_type values already used elsewhere in the app (GameStructure,
// ClubAboutGame, etc.) - reused here instead of introducing a second numbering.
export const LEAGUE_TYPE_BY_CATEGORY = {
  community_league: 1,
  association_league: 2,
  premier_state_competition: 3,
  national_tournament: 4,
  global_tournament: 5,
}

// Legacy backend game_type values that decide which admin portal gets provisioned.
export const getGameType = (competitionCategory, cricketFormat) => {
  if (competitionCategory === 'club_competition') return 1
  if (competitionCategory === 'match_contest') return 4
  if (LEAGUE_CATEGORIES.includes(competitionCategory)) {
    return cricketFormat === 'indoor' ? 3 : 2
  }
  return null
}

export const CLUB_SERVICE_PACKAGES = [
  {
    value: 'platform_licence',
    label: 'Platform Licence',
    description: 'Your club manages the game',
  },
  {
    value: 'managed_services',
    label: 'Managed Services',
    description: 'MyClubtap manages setup and ongoing delivery',
  },
  {
    value: 'partnership_services',
    label: 'Partnership Services',
    description: 'Managed delivery with content and partnership support',
  },
]

export const CLUB_TEAM_BANDS = [
  { value: '1-4', label: '1-4 teams', min: 1, max: 4 },
  { value: '5-8', label: '5-8 teams', min: 5, max: 8 },
  { value: '9-12', label: '9-12 teams', min: 9, max: 12 },
  { value: '13-16', label: '13-16 teams', min: 13, max: 16 },
  { value: '17-20', label: '17-20 teams', min: 17, max: 20 },
  { value: '21+', label: '21+ teams', min: 21, max: null },
]

// Base price per team band for each Club Competition package (Season / no-adjustment baseline).
const CLUB_BASE_PRICES = {
  managed_services: {
    '1-4': 360,
    '5-8': 505,
    '9-12': 650,
    '13-16': 790,
    '17-20': 935,
    '21+': 1080,
  },
  platform_licence: { '1-4': 160, '5-8': 210, '9-12': 265, '13-16': 315, '17-20': 370, '21+': 420 },
  partnership_services: {
    '1-4': 780,
    '5-8': 1040,
    '9-12': 1300,
    '13-16': 1560,
    '17-20': 1820,
    '21+': 2080,
  },
}

export const LEAGUE_TEAM_BANDS = [
  { value: '1-8', label: '1-8 teams', min: 1, max: 8, fixtureAllowance: 120 },
  { value: '9-16', label: '9-16 teams', min: 9, max: 16, fixtureAllowance: 200 },
  { value: '17+', label: '17+ teams', min: 17, max: null, fixtureAllowance: 300 },
]

// Base price per team band for the League / Tournament "Season Package" (only package
// available for this flow), per competition category. Duration does not adjust this
// price - it's collected for delivery planning only.
const LEAGUE_BASE_PRICES = {
  community_league: { '1-8': 1040, '9-16': 2080, '17+': 3120 },
  association_league: { '1-8': 2900, '9-16': 4400, '17+': 6200 },
  premier_state_competition: { '1-8': 5200, '9-16': 7400, '17+': 9600 },
  national_tournament: { '1-8': 7800, '9-16': 10400, '17+': 13000 },
  global_tournament: { '1-8': 7800, '9-16': 10400, '17+': 13000 },
}

// Match Contest is a single fixed-price package (no team-band/size selection) with
// preset inclusions. Duration is collected for planning only and doesn't change price.
export const MATCH_CONTEST_PACKAGE = {
  basePrice: 499,
  contestAllowance: 100,
  fixtureAllowance: 500,
  participantGuideline: 10000,
}

export const DURATION_OPTIONS = [
  { value: 'short', label: 'Short Competition', sublabel: 'Under 3 months', adjustmentPct: -10 },
  { value: 'season', label: 'Season Competition', sublabel: '4-6 months', adjustmentPct: 0 },
  {
    value: 'extended',
    label: 'Extended Competition',
    sublabel: 'More than 6 months',
    adjustmentPct: 10,
  },
]

export const TEAM_SIZE_OPTIONS = [6, 7, 8, 9, 10, 11]

export const getClubBasePrice = (servicePackage, teamBand) => {
  const table = CLUB_BASE_PRICES[servicePackage]
  if (!table) return null
  return table[teamBand] ?? null
}

export const getLeagueBasePrice = (competitionCategory, teamBand) =>
  LEAGUE_BASE_PRICES[competitionCategory]?.[teamBand] ?? null

// Client will not sell any package below this price, regardless of duration discount.
const MINIMUM_PRICE = 150

// Final price = Base package price x Duration adjustment (per the setup process doc).
// Only Club Competition's duration adjusts price - League/Tournament and Match Contest
// collect duration for delivery planning only, so their adjustment is always 0.
export const computePricing = (basePrice, duration, applyDurationAdjustment) => {
  if (basePrice === null || basePrice === undefined) {
    return { basePrice: null, priceAdjustment: null, finalPrice: null }
  }
  const option = DURATION_OPTIONS.find((d) => d.value === duration)
  const pct = applyDurationAdjustment && option ? option.adjustmentPct : 0
  let priceAdjustment = Math.round(basePrice * (pct / 100) * 100) / 100
  let finalPrice = Math.round((basePrice + priceAdjustment) * 100) / 100
  if (finalPrice < MINIMUM_PRICE) {
    finalPrice = MINIMUM_PRICE
    priceAdjustment = Math.round((finalPrice - basePrice) * 100) / 100
  }
  return { basePrice, priceAdjustment, finalPrice }
}

export const isReviewRequired = (competitionCategory, servicePackage) =>
  REVIEW_REQUIRED_CATEGORIES.includes(competitionCategory) ||
  servicePackage === 'partnership_services'

export const getCategoryLabel = (value) =>
  COMPETITION_CATEGORIES.find((c) => c.value === value)?.label || ''
